import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Lock } from "lucide-react";
import { trackPathwayClick } from "../lib/analytics";

/**
 * Build 4 — "Next Investigation" exit cards.
 *
 * Up to 3 curated rabbit-hole exits per topic, each with a short reason
 * chip ("same investor", "same era", "counter-evidence", …). Clicking
 * fires `tcp_pathway_click` so editorial intelligence can later observe
 * which clusters bridge to which, which exits convert best, and which
 * reason labels resonate.
 *
 * Renders nothing if no curated entries exist; the page falls back to
 * the simpler `next_topics` block in that case.
 */
export default function NextInvestigations({ fromSlug, fromCluster, entries }) {
    if (!entries || entries.length === 0) return null;
    return (
        <section
            data-testid="next-investigations"
            className="py-10 sm:py-14 border-t border-[#243044]"
        >
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#56CCF2] font-medium mb-3">
                Continue the investigation
            </div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white tracking-tight mb-8">
                The thread doesn't end here.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {entries.map((e, idx) => (
                    <Link
                        key={`${e.topic_slug}-${idx}`}
                        to={`/topic/${e.topic_slug}`}
                        data-testid={`next-investigation-${idx + 1}`}
                        onClick={() => {
                            trackPathwayClick({
                                from: fromSlug,
                                to: e.topic_slug,
                                from_cluster: fromCluster || null,
                                to_cluster: e.to_cluster || null,
                                position: e.position || idx + 1,
                                reason: e.reason_label || null,
                                locked: !!e.is_member_only,
                            });
                        }}
                        className="group block px-5 py-5 rounded-xl bg-[#0B1220] border border-[#243044] hover:border-[#2F80ED] hover:bg-[#111827] transition-all"
                    >
                        {e.reason_label ? (
                            <div className="inline-flex items-center gap-2 mb-3">
                                <span className="text-[10px] uppercase tracking-[0.2em] text-[#56CCF2] font-medium">
                                    {e.reason_label}
                                </span>
                                {e.is_member_only && (
                                    <Lock className="w-3 h-3 text-[#94A3B8]" />
                                )}
                            </div>
                        ) : null}
                        <h3 className="text-base font-semibold text-white leading-snug mb-2 group-hover:text-[#56CCF2] transition-colors">
                            {e.title}
                        </h3>
                        {e.subtitle ? (
                            <p className="text-[13px] leading-[1.6] text-[#94A3B8] line-clamp-2">
                                {e.subtitle}
                            </p>
                        ) : null}
                        <div className="mt-4 inline-flex items-center gap-1.5 text-xs text-[#94A3B8] group-hover:text-[#56CCF2] transition-colors">
                            Follow the thread <ArrowRight className="w-3.5 h-3.5" />
                        </div>
                    </Link>
                ))}
            </div>
        </section>
    );
}
